/**
 * Utilities for detecting and splitting quoted content in email bodies.
 * Used to collapse quoted previous messages (like Spark/Gmail).
 *
 * Supports both top-posting (reply above quote) and bottom-posting
 * (reply below quote) — content before AND after the quoted block
 * is treated as "original".
 */

/**
 * Split HTML email body into original content and quoted content.
 * Returns { original, quoted } where quoted is null if no quote was detected.
 */
export function splitQuotedContent(html: string): { original: string; quoted: string | null } {
  if (!html) return { original: html, quoted: null };

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__root">${html}</div>`, 'text/html');
  const root = doc.getElementById('__root');
  if (!root) return { original: html, quoted: null };

  // Strategy 1: Gmail quote div
  const gmailQuote = root.querySelector('div.gmail_quote');
  if (gmailQuote) {
    return extractQuotedBlock(root, gmailQuote);
  }

  // Strategy 2: Gmail extra (older format)
  const gmailExtra = root.querySelector('div.gmail_extra');
  if (gmailExtra) {
    return extractQuotedBlock(root, gmailExtra);
  }

  // Strategy 3: Yahoo quote
  const yahooQuote = root.querySelector('div.yahoo_quoted');
  if (yahooQuote) {
    return extractQuotedBlock(root, yahooQuote);
  }

  // Strategy 4: Outlook separator (border-top style pattern)
  const outlookSep = root.querySelector('div[id="appendonsend"]');
  if (outlookSep) {
    return extractQuotedBlock(root, outlookSep);
  }

  // Strategy 5: Look for blockquote preceded by "wrote:" / "napisał(a):" text
  const blockquotes = root.querySelectorAll('blockquote');
  for (const bq of blockquotes) {
    // Check preceding sibling or parent text for "wrote:" pattern
    const prevSibling = bq.previousSibling || bq.previousElementSibling;
    if (prevSibling) {
      const prevText = prevSibling.textContent || '';
      if (isWroteLine(prevText)) {
        // "wrote:" line = start, blockquote = end of quoted block
        return extractQuotedBlock(root, prevSibling as Element, bq);
      }
    }
    // If blockquote is direct child of root without "wrote:" prefix, still treat it as quote
    if (bq.parentElement === root) {
      return extractQuotedBlock(root, bq);
    }
  }

  // Strategy 6: Scan text nodes for "wrote:" / "napisał(a):" / "Original Message" patterns
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let textNode: Text | null;
  while ((textNode = walker.nextNode() as Text | null)) {
    const text = textNode.textContent || '';
    if (isWroteLine(text) || isOriginalMessageSeparator(text)) {
      // Find the containing element to split at
      const container = textNode.parentElement;
      if (container && container !== root) {
        // Look for a following blockquote sibling to use as end of quoted block
        let nextEl = container.nextElementSibling;
        while (nextEl && !nextEl.textContent?.trim()) {
          nextEl = nextEl.nextElementSibling;
        }
        if (nextEl && nextEl.tagName === 'BLOCKQUOTE') {
          return extractQuotedBlock(root, container, nextEl);
        }
        return extractQuotedBlock(root, container);
      }
    }
  }

  return { original: html, quoted: null };
}

/**
 * Split plain text email body into original and quoted content.
 * Handles both top-posting and bottom-posting.
 */
export function splitQuotedPlainText(text: string): { original: string; quoted: string | null } {
  if (!text) return { original: text, quoted: null };

  const lines = text.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // "wrote:" / "napisał(a):" pattern line
    if (isWroteLine(line) || isOriginalMessageSeparator(line)) {
      // Find the end of the quoted block
      let endQuote = i + 1;
      // Look for ">" quoted lines after the separator
      while (endQuote < lines.length && /^>/.test(lines[endQuote].trim())) {
        endQuote++;
      }
      // If no ">" lines found, include until double blank line or end
      if (endQuote === i + 1) {
        while (endQuote < lines.length) {
          // Stop at double blank line followed by non-empty content
          if (!lines[endQuote].trim() && endQuote + 1 < lines.length && !lines[endQuote + 1].trim()) {
            // Found double blank — check if there's content after
            let j = endQuote + 2;
            while (j < lines.length && !lines[j].trim()) j++;
            if (j < lines.length) {
              endQuote = endQuote;
              break;
            }
          }
          endQuote++;
        }
      }

      const before = lines.slice(0, i).join('\n').trimEnd();
      const quoted = lines.slice(i, endQuote).join('\n');
      const after = lines.slice(endQuote).join('\n').trimStart();
      const original = [before, after].filter(s => s.trim()).join('\n\n');

      if (original.trim()) {
        return { original, quoted };
      }
    }

    // Lines starting with ">" (quoted text)
    if (/^>/.test(line) && i > 0) {
      // Check if this is a block of quoted lines (not just a single >)
      let endQuote = i;
      while (endQuote < lines.length && /^>/.test(lines[endQuote].trim())) {
        endQuote++;
      }
      // Only split if we have at least 2 quoted lines
      if (endQuote - i >= 2) {
        // Also include the preceding "wrote:" line if exists
        const start = i > 0 && isWroteLine(lines[i - 1].trim()) ? i - 1 : i;
        const before = lines.slice(0, start).join('\n').trimEnd();
        const quoted = lines.slice(start, endQuote).join('\n');
        const after = lines.slice(endQuote).join('\n').trimStart();
        const original = [before, after].filter(s => s.trim()).join('\n\n');

        if (original.trim()) {
          return { original, quoted };
        }
      }
    }
  }

  return { original: text, quoted: null };
}

/**
 * Check if a text line matches "wrote:" patterns common in email replies.
 */
function isWroteLine(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  return /wrote:\s*$/i.test(trimmed)
    || /napisał(\(a\))?:\s*$/i.test(trimmed)
    || /W dniu .+ napisał/i.test(trimmed)
    || /Dnia .+ napisał/i.test(trimmed)
    || /użytkownik .+ napisał/i.test(trimmed)
    || /schrieb:\s*$/i.test(trimmed)
    || /a écrit\s*:\s*$/i.test(trimmed);
}

/**
 * Check if a text line is an "Original Message" separator.
 */
function isOriginalMessageSeparator(text: string): boolean {
  const trimmed = text.trim();
  return /^-{2,}\s*(Original Message|Oryginalna wiadomość|Forwarded message|Wiadomość przekazana)/i.test(trimmed);
}

/**
 * Extract a quoted block from root, keeping content before AND after as "original".
 * This handles both top-posting and bottom-posting correctly.
 *
 * @param root - The root container element
 * @param startNode - First node of the quoted block
 * @param endNode - Last node of the quoted block (defaults to startNode if not provided)
 */
function extractQuotedBlock(
  root: HTMLElement,
  startNode: Element | Node,
  endNode?: Element | Node,
): { original: string; quoted: string | null } {
  const originalParts: string[] = [];
  const quotedParts: string[] = [];
  let phase: 'before' | 'in_quote' | 'after' = 'before';

  for (const child of Array.from(root.childNodes)) {
    const matchesStart = child === startNode || child.contains(startNode as Node);
    const matchesEnd = endNode
      ? (child === endNode || child.contains(endNode as Node))
      : false;

    if (phase === 'before' && matchesStart) {
      phase = 'in_quote';
      quotedParts.push(nodeToHtml(child));
      // If no separate endNode, or this node is also the end — finish quote after this element
      if (!endNode || matchesEnd) {
        phase = 'after';
      }
      continue;
    }

    if (phase === 'in_quote') {
      quotedParts.push(nodeToHtml(child));
      if (matchesEnd) {
        phase = 'after';
      }
      continue;
    }

    // phase === 'before' || phase === 'after'
    originalParts.push(nodeToHtml(child));
  }

  const original = originalParts.join('');
  const quoted = quotedParts.join('');

  if (!original.trim()) {
    // If there's no original content (entire email is quoted), don't split
    return { original: root.innerHTML, quoted: null };
  }

  if (!quoted.trim()) {
    return { original: root.innerHTML, quoted: null };
  }

  return { original, quoted };
}

/**
 * Convert a DOM node back to HTML string.
 */
function nodeToHtml(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || '';
  }
  if (node.nodeType === Node.ELEMENT_NODE) {
    return (node as Element).outerHTML;
  }
  return '';
}
